import React, { useState, useEffect, useMemo } from 'react';
import nbrApi from '../../../services/nbrApi';
import moment from 'moment';
import FullPageLoader from '../../components/common/FullPageLoader';
import { toast } from 'react-toastify';
import '../../../css/multi-property-monitor.css';
import TimeAllocationBar from '../../components/common/TimeAllocationBar';

const SETTINGS_COOKIE = 'functionTimeReportSettings';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

const GROUPING_OPTIONS = [
    { value: 'resource_function', label: 'Resources / Function' },
    { value: 'resource_request', label: 'Resources / Request' },
    { value: 'function_resource', label: 'Function / Resources' },
    { value: 'function_shift', label: 'Function / Shift' },
    { value: 'request', label: 'Request' }
];

const VALID_GROUPING_MODES = GROUPING_OPTIONS.map(option => option.value);
const LEGACY_GROUPING_MODE_MAP = {
    resource_shift: 'resource_function',
    function_request: 'function_resource',
    shift_resource: 'function_shift'
};

const RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' }
];

const TIME_COLUMNS_TO_HIDE = ['time_allowed', 'time_worked'];
const TIME_VISUAL_COLUMN = 'time_progress';

// Format seconds to HH:MM:SS
const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00:00';
    const hrs = Math.floor(Math.abs(seconds) / 3600);
    const mins = Math.floor((Math.abs(seconds) % 3600) / 60);
    const secs = Math.floor(Math.abs(seconds) % 60);
    const sign = seconds < 0 ? '-' : '';
    return `${sign}${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

function FunctionTimeSegmentsReport() {
    const selectedLoginData = JSON.parse(localStorage.getItem('selectedLoginData')) || {};
    const propertiesId = selectedLoginData?.properties_id;

    // State management
    const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [visibleColumns, setVisibleColumns] = useState({});
    const [controlsExpanded, setControlsExpanded] = useState(false);
    const [activeRange, setActiveRange] = useState('today');
    const [groupingMode, setGroupingMode] = useState('resource_function');
    const [savedVisibleColumns, setSavedVisibleColumns] = useState(null);
    const [settingsLoaded, setSettingsLoaded] = useState(false);

    const readSettingsCookie = () => {
        if (typeof document === 'undefined') {
            return null;
        }

        const cookieEntry = document.cookie
            .split('; ')
            .find(entry => entry.startsWith(`${SETTINGS_COOKIE}=`));

        if (!cookieEntry) {
            return null;
        }

        try {
            return JSON.parse(decodeURIComponent(cookieEntry.split('=')[1]));
        } catch (error) {
            console.error('Failed to parse report settings cookie:', error);
            return null;
        }
    };

    const writeSettingsCookie = (settings) => {
        if (typeof document === 'undefined') {
            return;
        }

        try {
            const value = encodeURIComponent(JSON.stringify(settings));
            document.cookie = `${SETTINGS_COOKIE}=${value}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}`;
        } catch (error) {
            console.error('Failed to write report settings cookie:', error);
        }
    };

    const applyDateRange = (rangeKey) => {
        switch (rangeKey) {
            case 'today': {
                const todayStart = moment().startOf('day');
                setFromDate(todayStart.format('YYYY-MM-DD'));
                setToDate(todayStart.format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_week': {
                const startOfWeek = moment().startOf('isoWeek');
                setFromDate(startOfWeek.format('YYYY-MM-DD'));
                setToDate(startOfWeek.clone().endOf('isoWeek').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            case 'this_month': {
                const startOfMonth = moment().startOf('month');
                setFromDate(startOfMonth.format('YYYY-MM-DD'));
                setToDate(startOfMonth.clone().endOf('month').format('YYYY-MM-DD'));
                setActiveRange(rangeKey);
                break;
            }
            default:
                break;
        }
    };

    const handleGroupingModeChange = (mode) => {
        setGroupingMode(mode);
        setExpandedGroups({});
    };

    useEffect(() => {
        const settings = readSettingsCookie();

        if (settings) {
            // if (typeof settings.controlsExpanded === 'boolean') {
            //     setControlsExpanded(settings.controlsExpanded);
            // }

            if (settings.visibleColumns) {
                setSavedVisibleColumns(settings.visibleColumns);
            }

            if (settings.groupingMode) {
                const resolvedGroupingMode = VALID_GROUPING_MODES.includes(settings.groupingMode)
                    ? settings.groupingMode
                    : LEGACY_GROUPING_MODE_MAP[settings.groupingMode];

                if (resolvedGroupingMode && VALID_GROUPING_MODES.includes(resolvedGroupingMode)) {
                    setGroupingMode(resolvedGroupingMode);
                }
            }

            if (settings.activeRange && settings.activeRange !== 'custom') {
                applyDateRange(settings.activeRange);
            } else {
                if (settings.fromDate) {
                    setFromDate(settings.fromDate);
                }

                if (settings.toDate) {
                    setToDate(settings.toDate);
                }

                if (settings.activeRange) {
                    setActiveRange(settings.activeRange);
                } else {
                    setActiveRange('custom');
                }
            }
        } else {
            applyDateRange('today');
        }

        setSettingsLoaded(true);
    }, []);

    const baseColumns = useMemo(() => {
        if (reportData.length === 0) return [];
        return Object.keys(reportData[0]);
    }, [reportData]);

    const allColumns = useMemo(() => {
        if (baseColumns.length === 0) return [];

        const filteredColumns = baseColumns
            .filter(column => !TIME_COLUMNS_TO_HIDE.includes(column))
            .filter(Boolean);

        if (filteredColumns.length === 0) {
            return [TIME_VISUAL_COLUMN];
        }

        const columnsWithoutTimeVisual = filteredColumns.filter(column => column !== TIME_VISUAL_COLUMN);
        const [firstColumn, ...remainingColumns] = columnsWithoutTimeVisual.length > 0
            ? columnsWithoutTimeVisual
            : [filteredColumns[0], ...filteredColumns.slice(1)];

        const columnOrder = [];

        if (firstColumn) {
            columnOrder.push(firstColumn);
        }

        columnOrder.push(TIME_VISUAL_COLUMN);

        const remainingSet = new Set(columnOrder);
        remainingColumns.forEach(column => {
            if (!remainingSet.has(column)) {
                columnOrder.push(column);
            }
        });

        filteredColumns.forEach(column => {
            if (!columnOrder.includes(column)) {
                columnOrder.push(column);
            }
        });

        return columnOrder;
    }, [baseColumns]);

    const visibleColumnOrder = useMemo(
        () => allColumns.filter(column => visibleColumns[column]),
        [allColumns, visibleColumns]
    );

    useEffect(() => {
        if (allColumns.length === 0) {
            setVisibleColumns({});
            return;
        }

        setVisibleColumns(prev => {
            const next = {};

            allColumns.forEach(column => {
                if (prev && Object.prototype.hasOwnProperty.call(prev, column)) {
                    next[column] = prev[column];
                } else if (savedVisibleColumns && Object.prototype.hasOwnProperty.call(savedVisibleColumns, column)) {
                    next[column] = savedVisibleColumns[column];
                } else {
                    next[column] = true;
                }
            });

            return next;
        });
    }, [allColumns, savedVisibleColumns]);

    useEffect(() => {
        if (!settingsLoaded) {
            return;
        }

        writeSettingsCookie({
            controlsExpanded,
            visibleColumns,
            groupingMode,
            activeRange,
            fromDate,
            toDate
        });
    }, [controlsExpanded, visibleColumns, groupingMode, activeRange, fromDate, toDate, settingsLoaded]);

    // Format timestamp to local date time
    const formatDateTime = (timestamp) => {
        if (!timestamp) return '-';
        return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

    // Fetch report data
    const fetchReportData = async () => {
        if (!propertiesId) {
            toast.error('Property ID not found. Please log in again.');
            return;
        }

        setLoading(true);
        try {
            const response = await nbrApi.post('/getfunctiontimesegmentslist', {
                properties_id: propertiesId,
                from_date: fromDate,
                to_date: toDate
            });

            if (response?.data) {
                setReportData(Array.isArray(response.data) ? response.data.map(row => ({ ...row })) : []);
                toast.success(`Report loaded successfully with ${response.data.length} records`);
            } else {
                setReportData([]);
                toast.info('No data found for the selected date range');
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            toast.error('Failed to fetch report data. Please try again.');
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const calculateRowTotals = (rows) => {
        const totals = rows.reduce(
            (acc, row) => {
                const timeAllowed = parseFloat(row.time_allowed) || 0;
                const timeWorked = parseFloat(row.time_worked) || 0;

                acc.time_allowed += timeAllowed;
                acc.time_worked += timeWorked;
                return acc;
            },
            { time_allowed: 0, time_worked: 0 }
        );

        return {
            ...totals,
            variance: totals.time_worked - totals.time_allowed
        };
    };

    const mergeTotals = (a = {}, b = {}) => {
        const time_allowed = (a.time_allowed || 0) + (b.time_allowed || 0);
        const time_worked = (a.time_worked || 0) + (b.time_worked || 0);

        return {
            time_allowed,
            time_worked,
            variance: time_worked - time_allowed
        };
    };

    const groupedNodes = useMemo(() => {
        if (reportData.length === 0) {
            return [];
        }

        const createNode = (key, label, rows = [], children = []) => {
            const rowTotals = rows.length ? calculateRowTotals(rows) : { time_allowed: 0, time_worked: 0, variance: 0 };
            const childTotals = children.length
                ? children.reduce((acc, child) => mergeTotals(acc, child.totals), { time_allowed: 0, time_worked: 0, variance: 0 })
                : { time_allowed: 0, time_worked: 0, variance: 0 };

            return {
                key,
                label,
                rows,
                children,
                totals: mergeTotals(rowTotals, childTotals)
            };
        };

        if (groupingMode === 'resource_function') {
            const resourceMap = {};

            reportData.forEach(row => {
                const resourceId = row.resources_id || 'Unknown Resource';
                const functionName = row.function_name || 'Unknown Function';

                if (!resourceMap[resourceId]) {
                    resourceMap[resourceId] = {
                        resourceId,
                        sampleRow: row,
                        functions: {}
                    };
                }

                if (!resourceMap[resourceId].functions[functionName]) {
                    resourceMap[resourceId].functions[functionName] = {
                        functionName,
                        sampleRow: row,
                        rows: []
                    };
                }

                resourceMap[resourceId].functions[functionName].rows.push(row);
            });

            return Object.values(resourceMap).map(resource => {
                const resourceName = resource.sampleRow?.resource || '';
                const roleName = resource.sampleRow?.role_name || '';
                const resourceLabel = `${resourceName || `ID ${resource.resourceId}`}${roleName ? ` (${roleName})` : ''}`;

                const children = Object.values(resource.functions).map(func => {
                    const functionLabel = `Function: ${func.functionName}`;
                    return createNode(
                        `function_${resource.resourceId}_${func.functionName}`,
                        functionLabel,
                        func.rows
                    );
                });

                return createNode(
                    `resource_${resource.resourceId}`,
                    resourceLabel,
                    [],
                    children
                );
            });
        }

        if (groupingMode === 'resource_request') {
            const resourceMap = {};

            reportData.forEach(row => {
                const resourceId = row.resources_id || 'Unknown Resource';
                const requestKey = row.request_id || 'Unknown Request';

                if (!resourceMap[resourceId]) {
                    resourceMap[resourceId] = {
                        resourceId,
                        sampleRow: row,
                        requests: {}
                    };
                }

                if (!resourceMap[resourceId].requests[requestKey]) {
                    resourceMap[resourceId].requests[requestKey] = {
                        requestKey,
                        sampleRow: row,
                        rows: []
                    };
                }

                resourceMap[resourceId].requests[requestKey].rows.push(row);
            });

            return Object.values(resourceMap).map(resource => {
                const resourceName = resource.sampleRow?.resource || '';
                const roleName = resource.sampleRow?.role_name || '';
                const resourceLabel = `${resourceName || `ID ${resource.resourceId}`}${roleName ? ` (${roleName})` : ''}`;

                const children = Object.values(resource.requests).map(request => {
                    const requestType = request.sampleRow?.request || '';
                    const requestLabel = `Request: ${request.requestKey}${requestType ? ` (${requestType})` : ''}`;
                    return createNode(
                        `request_${resource.resourceId}_${request.requestKey}`,
                        requestLabel,
                        request.rows
                    );
                });

                return createNode(
                    `resource_${resource.resourceId}`,
                    resourceLabel,
                    [],
                    children
                );
            });
        }

        if (groupingMode === 'function_resource') {
            const functionMap = {};

            reportData.forEach(row => {
                const functionName = row.function_name || 'Unknown Function';
                const resourceId = row.resources_id || 'Unknown Resource';

                if (!functionMap[functionName]) {
                    functionMap[functionName] = {
                        functionName,
                        resources: {}
                    };
                }

                if (!functionMap[functionName].resources[resourceId]) {
                    functionMap[functionName].resources[resourceId] = {
                        resourceId,
                        sampleRow: row,
                        rows: []
                    };
                }

                functionMap[functionName].resources[resourceId].rows.push(row);
            });

            return Object.values(functionMap).map(func => {
                const funcLabel = `Function: ${func.functionName}`;

                const children = Object.values(func.resources).map(resource => {
                    const resourceName = resource.sampleRow?.resource || '';
                    const roleName = resource.sampleRow?.role_name || '';
                    const resourceLabel = `${resourceName || `ID ${resource.resourceId}`}${roleName ? ` (${roleName})` : ''}`;
                    return createNode(
                        `resource_${func.functionName}_${resource.resourceId}`,
                        resourceLabel,
                        resource.rows
                    );
                });

                return createNode(
                    `function_${func.functionName}`,
                    funcLabel,
                    [],
                    children
                );
            });
        }

        if (groupingMode === 'function_shift') {
            const functionMap = {};

            reportData.forEach(row => {
                const functionName = row.function_name || 'Unknown Function';
                const shiftKey = row.shift_segment || 'Unknown Shift';

                if (!functionMap[functionName]) {
                    functionMap[functionName] = {
                        functionName,
                        sampleRow: row,
                        shifts: {}
                    };
                }

                if (!functionMap[functionName].shifts[shiftKey]) {
                    functionMap[functionName].shifts[shiftKey] = {
                        shiftKey,
                        sampleRow: row,
                        rows: []
                    };
                }

                functionMap[functionName].shifts[shiftKey].rows.push(row);
            });

            return Object.values(functionMap).map(func => {
                const funcLabel = `Function: ${func.functionName}`;

                const children = Object.values(func.shifts).map(shift => {
                    const segmentType = shift.sampleRow?.shift_segment_type || '';
                    const shiftLabel = `Shift: ${shift.shiftKey}${segmentType ? ` (${segmentType})` : ''}`;
                    return createNode(
                        `shift_${func.functionName}_${shift.shiftKey}`,
                        shiftLabel,
                        shift.rows
                    );
                });

                return createNode(
                    `function_${func.functionName}`,
                    funcLabel,
                    [],
                    children
                );
            });
        }

        if (groupingMode === 'request') {
            const requestMap = {};

            reportData.forEach(row => {
                const requestKey = row.request_id || 'Unknown Request';

                if (!requestMap[requestKey]) {
                    requestMap[requestKey] = {
                        requestKey,
                        sampleRow: row,
                        rows: []
                    };
                }

                requestMap[requestKey].rows.push(row);
            });

            return Object.values(requestMap).map(request => {
                const requestType = request.sampleRow?.request || '';
                const requestLabel = `Request: ${request.requestKey}${requestType ? ` (${requestType})` : ''}`;

                return createNode(
                    `request_${request.requestKey}`,
                    requestLabel,
                    request.rows
                );
            });
        }

        return [];
    }, [reportData, groupingMode]);

    useEffect(() => {
        if (groupedNodes.length === 0) {
            setExpandedGroups({});
            return;
        }

        setExpandedGroups(prev => {
            const next = {};

            const addKeys = (nodes) => {
                nodes.forEach(node => {
                    const existingValue = Object.prototype.hasOwnProperty.call(prev, node.key)
                        ? prev[node.key]
                        : false;
                    next[node.key] = existingValue;

                    if (node.children && node.children.length > 0) {
                        addKeys(node.children);
                    }
                });
            };

            addKeys(groupedNodes);

            return next;
        });
    }, [groupedNodes]);

    // Toggle group expansion
    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    // Toggle all groups
    const toggleAllGroups = (expand) => {
        const newExpandedGroups = {};

        const addKeys = (nodes) => {
            nodes.forEach(node => {
                newExpandedGroups[node.key] = expand;

                if (node.children && node.children.length > 0) {
                    addKeys(node.children);
                }
            });
        };

        addKeys(groupedNodes);
        setExpandedGroups(newExpandedGroups);
    };

    const expandNextGroup = () => {
        if (groupedNodes.length === 0) {
            return;
        }

        setExpandedGroups(prev => {
            const next = { ...prev };
            const levels = [];

            const collectLevels = (nodes, level = 0) => {
                if (!levels[level]) {
                    levels[level] = [];
                }

                nodes.forEach(node => {
                    levels[level].push(node);
                    if (node.children && node.children.length > 0) {
                        collectLevels(node.children, level + 1);
                    }
                });
            };

            collectLevels(groupedNodes);

            const targetLevel = levels.find(levelNodes =>
                levelNodes && levelNodes.some(node => !next[node.key])
            );

            if (!targetLevel) {
                return next;
            }

            targetLevel.forEach(node => {
                next[node.key] = true;
            });

            return next;
        });
    };

    // Toggle column visibility
    const toggleColumn = (columnName) => {
        setVisibleColumns(prev => ({
            ...prev,
            [columnName]: !prev[columnName]
        }));
    };

    // Get all unique columns

    // Time fields that need special formatting
    const timeFields = ['time_allowed', 'time_worked', 'variance'];
    const getColumnLabel = (column) => {
        if (column === TIME_VISUAL_COLUMN) {
            return 'Time vs Allocation';
        }
        return column.replace(/_/g, ' ');
    };
    const timestampFields = ['time_start', 'time_stop'];

    const getBarSizeForLevel = (level, isGroup = false) => {
        if (isGroup) {
            if (level <= 0) return 'xl';
            if (level === 1) return 'lg';
            if (level === 2) return 'md';
            return 'sm';
        }

        if (level <= 1) return 'sm';
        if (level === 2) return 'xs';
        return 'xs';
    };

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if (reportData.length === 0) return null;

        const totalTimeAllowed = reportData.reduce((sum, row) => sum + (parseFloat(row.time_allowed) || 0), 0);
        const totalTimeWorked = reportData.reduce((sum, row) => sum + (parseFloat(row.time_worked) || 0), 0);
        const totalVariance = totalTimeWorked - totalTimeAllowed;
        const uniqueResources = new Set(reportData.map(row => row.resources_id)).size;
        const uniqueRequests = new Set(reportData.map(row => row.request_id)).size;

        return {
            totalRecords: reportData.length,
            uniqueResources,
            uniqueRequests,
            totalTimeAllowed,
            totalTimeWorked,
            totalVariance,
            avgVariance: totalVariance / reportData.length
        };
    }, [reportData]);

    // Export to CSV
    const exportToCSV = () => {
        if (reportData.length === 0) {
            toast.warning('No data to export');
            return;
        }

        const headers = visibleColumnOrder;
        const csvContent = [
            headers.join(','),
            ...reportData.map(row =>
                headers.map(header => {
                    if (header === TIME_VISUAL_COLUMN) {
                        const allowed = formatTime(row.time_allowed);
                        const worked = formatTime(row.time_worked);
                        return `"${allowed} / ${worked}"`;
                    }

                    let value = row[header];
                    if (timeFields.includes(header)) {
                        value = formatTime(value);
                    }

                    const safeValue = value !== null && value !== undefined ? value : '-';
                    return `"${safeValue}"`;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `function_time_segments_${fromDate}_to_${toDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Report exported successfully');
    };

    // Render data row
    const firstTextColumnIndex = useMemo(
        () => visibleColumnOrder.findIndex(column => column !== TIME_VISUAL_COLUMN),
        [visibleColumnOrder]
    );

    const renderDataRow = (row, rowKey, level = 1) => (
        <tr key={rowKey} className="data-row">
            {visibleColumnOrder.map((column, colIndex) => {
                const shouldIndent = firstTextColumnIndex === -1
                    ? colIndex === 0
                    : colIndex === firstTextColumnIndex;

                if (column === TIME_VISUAL_COLUMN) {
                    return (
                        <td
                            key={colIndex}
                            className="small"
                            style={{ padding: '4px 8px' }}
                        >
                            <TimeAllocationBar
                                timeAllowed={row.time_allowed}
                                timeWorked={row.time_worked}
                                size={getBarSizeForLevel(level, false)}
                            />
                        </td>
                    );
                }

                let value = row[column];

                if (timeFields.includes(column)) {
                    value = formatTime(value);
                } else if (timestampFields.includes(column)) {
                    value = formatDateTime(value);
                } else if (column === 'variance %') {
                    value = value ? `${value}%` : '-';
                }

                const cellValue = value !== null && value !== undefined ? value : '-';

                return (
                    <td
                        key={colIndex}
                        className="small"
                        style={{
                            paddingLeft: shouldIndent ? `${8 + level * 20}px` : '8px',
                            paddingTop: '4px',
                            paddingBottom: '4px'
                        }}
                    >
                        {cellValue}
                    </td>
                );
            })}
        </tr>
    );

    const hasCollapsedTopLevel = useMemo(
        () => groupedNodes.some(node => !expandedGroups[node.key]),
        [groupedNodes, expandedGroups]
    );

    const renderGroupNode = (node, level = 0) => {
        const isExpanded = Object.prototype.hasOwnProperty.call(expandedGroups, node.key)
            ? expandedGroups[node.key]
            : false;

        const backgroundPalette = ['#d9e2ef', '#e6ecf6', '#f1f5fb', '#f8fafc'];
        const backgroundColor = backgroundPalette[level] || '#fafafa';
        const fontWeight = level === 0 ? 'bold' : '500';

        return (
            <React.Fragment key={node.key}>
                <tr
                    className="group-row"
                    onClick={() => toggleGroup(node.key)}
                    style={{
                        backgroundColor,
                        cursor: 'pointer',
                        fontWeight
                    }}
                >
                    {visibleColumnOrder.map((column, colIndex) => {
                        const isLabelColumn = firstTextColumnIndex === -1
                            ? colIndex === 0
                            : colIndex === firstTextColumnIndex;


                        if (isLabelColumn) {
                            return (
                                <td
                                    key={colIndex}
                                    className="small"
                                    style={{
                                        paddingLeft: `${8 + level * 20}px`,
                                        paddingTop: '6px',
                                        paddingBottom: '6px'
                                    }}
                                >
                                    <div className='d-flex gap-3'>
                                        <div className='d-flex align-items-center'>
                                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
                                            {node.label}
                                        </div>
                                        <TimeAllocationBar
                                            timeAllowed={node.totals.time_allowed}
                                            timeWorked={node.totals.time_worked}
                                            // size={getBarSizeForLevel(level, true)}
                                        />
                                    </div>
                                </td>
                            );
                        }

                        if (timeFields.includes(column)) {
                            return (
                                <td key={colIndex} className="small" style={{ paddingTop: '6px', paddingBottom: '6px' }}>
                                    {formatTime(node.totals[column])}
                                </td>
                            );
                        }

                        return (
                            <td key={colIndex} className="small" style={{ paddingTop: '6px', paddingBottom: '6px' }}>
                                
                            </td>
                        );
                    })}
                </tr>
                {isExpanded && node.children.map(child => renderGroupNode(child, level + 1))}
                {isExpanded && node.rows.map((row, index) => renderDataRow(row, `${node.key}_row_${index}`, level + 1))}
            </React.Fragment>
        );
    };

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <h3 className="mb-0 text-primary fw-bold">
                                <i className="bi bi-clock-history me-2"></i>
                                Performance Report
                            </h3>
                            <p className="text-muted mb-0 mt-2">
                                View detailed time tracking for housekeeping functions and requests
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Date Range Selector */}
            <div className="row mb-2">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row align-items-end g-3">
                                <div className="col-lg-2 col-md-6">
                                    <label htmlFor="fromDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-event me-2"></i>
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        id="fromDate"
                                        className="form-control"
                                        value={fromDate}
                                        style={{ maxWidth: '150px' }}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>
                                <div className="col-lg-2 col-md-6">
                                    <label htmlFor="toDate" className="form-label fw-bold text-primary">
                                        <i className="bi bi-calendar-check me-2"></i>
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        id="toDate"
                                        className="form-control"
                                        value={toDate}
                                        style={{ maxWidth: '150px' }}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            if (activeRange !== 'custom') {
                                                setActiveRange('custom');
                                            }
                                        }}
                                    />
                                </div>
                                <div className="col-lg-4 col-md-12">
                                    <label className="form-label fw-bold text-primary d-block">
                                        <i className="bi bi-lightning-charge me-2"></i>
                                        Quick Ranges
                                    </label>
                                    <div className="d-flex flex-lg-nowrap flex-wrap w-100 gap-2">
                                        {RANGE_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`w-lg-100 btn btn-sm ${activeRange === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => applyDateRange(option.value)}
                                                style={{ minWidth: '110px' }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-12">
                                    <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary flex-grow-1"
                                            onClick={fetchReportData}
                                            disabled={loading}
                                        >
                                            <i className="bi bi-search me-2"></i>
                                            {loading ? 'Loading...' : 'Generate Report'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary flex-grow-1"
                                            onClick={exportToCSV}
                                            disabled={reportData.length === 0}
                                        >
                                            <i className="bi bi-download me-2"></i>
                                            Export
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Statistics */}
            {summaryStats && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body">
                                <h5 className="fw-bold text-dark mb-3">Summary Statistics</h5>
                                <div className="row g-3">
                                    {/* Total Records */}
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3">
                                            <h3 className="mb-1 fw-bold">{summaryStats.totalRecords}</h3>
                                            <span className="text-muted fw-bold" style={{ fontSize: '0.9rem' }}>Total Records</span>
                                        </div>
                                    </div>

                                    {/* Resources */}
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3">
                                            <h3 className="mb-1 fw-bold">{summaryStats.uniqueResources}</h3>
                                            <span className="text-muted fw-bold" style={{ fontSize: '0.9rem' }}>Resources</span>
                                        </div>
                                    </div>

                                    {/* Requests */}
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3">
                                            <h3 className="mb-1 fw-bold">{summaryStats.uniqueRequests}</h3>
                                            <span className="text-muted fw-bold" style={{ fontSize: '0.9rem' }}>Requests</span>
                                        </div>
                                    </div>

                                    {/* Time Allowed */}
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3">
                                            <h3 className="mb-1 fw-bold">{formatTime(summaryStats.totalTimeAllowed)}</h3>
                                            <span className="text-muted fw-bold" style={{ fontSize: '0.9rem' }}>Time Allowed</span>
                                        </div>
                                    </div>

                                    {/* Time Worked */}
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3">
                                            <h3 className="mb-1 fw-bold">{formatTime(summaryStats.totalTimeWorked)}</h3>
                                            <span className="text-muted fw-bold" style={{ fontSize: '0.9rem' }}>Time Worked</span>
                                        </div>
                                    </div>

                                    {/* Total Variance */}
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3">
                                            <h3 className="mb-1 fw-bold">
                                                {formatTime(Math.abs(summaryStats.totalVariance))}
                                            </h3>
                                            <span className="text-muted fw-bold" style={{ fontSize: '0.9rem' }}>
                                                Total Variance {summaryStats.totalVariance >= 0 ? '(Over)' : '(Under)'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 pe-4">
                                    <div className="row align-items-center g-3 justify-space-between">
                                        <div className="col-12 col-lg-9">
                                            <h6 className="fw-bold text-dark mb-1">Overall Utilization</h6>
                                            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                                Total time worked vs time allowed across the selected range
                                            </p>
                                        </div>
                                        <div className="col-12 col-lg-3">
                                            <TimeAllocationBar
                                                timeAllowed={summaryStats.totalTimeAllowed}
                                                timeWorked={summaryStats.totalTimeWorked}
                                                size="lg"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Controls */}
            {reportData.length > 0 && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div
                                className="card-header bg-white"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setControlsExpanded(!controlsExpanded)}
                            >
                                <div className="d-flex justify-content-between align-items-center py-2 w-100">
                                    <h6 className="mb-0 fw-bold">
                                        <i className={`bi bi-chevron-${controlsExpanded ? 'down' : 'right'} me-2`}></i>
                                        Report Controls
                                    </h6>

                                    <div className="d-flex flex-wrap gap-2">
                                        {GROUPING_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`btn btn-sm ${groupingMode === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleGroupingModeChange(option.value);
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {controlsExpanded && (
                                <div className="card-body">
                                    <label className="fw-bold text-dark mb-3 d-block">
                                        Column Visibility
                                    </label>
                                    <div className="row g-3">
                                        {allColumns.map(column => (
                                            <div key={column} className="col-md-3 col-sm-4 col-6">
                                                <div
                                                    className="form-check p-2 rounded"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`column_${column}`}
                                                        checked={visibleColumns[column] || false}
                                                        onChange={() => toggleColumn(column)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            marginTop: 7
                                                        }}
                                                    />
                                                    <label
                                                        className="form-check-label ms-2"
                                                        htmlFor={`column_${column}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    >
                                                        {getColumnLabel(column).toUpperCase()}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Data Table */}
            {loading ? (
                <FullPageLoader />
            ) : reportData.length === 0 ? (
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-inbox" style={{ fontSize: '4rem', color: '#ccc' }}></i>
                                <h4 className="mt-3 text-muted">No Data Available</h4>
                                <p className="text-muted">
                                    Select a date range and click "Generate Report" to view data
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className='col-12 d-flex justify-content-end mb-3'>
                        <div className="btn-group" role="group">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={fetchReportData}
                                style={{ minWidth: '110px' }}
                                disabled={loading}
                            >
                                <i className="bi bi-arrow-repeat me-1"></i>
                                Refresh
                            </button>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={expandNextGroup}
                                style={{ minWidth: '110px' }}
                                disabled={!hasCollapsedTopLevel}
                            >
                                <i className="bi bi-list-nested me-1"></i>
                                Expand Next
                            </button>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => toggleAllGroups(true)}
                                style={{ minWidth: '110px' }}
                            >
                                <i className="bi bi-arrows-expand me-1"></i>
                                Expand All
                            </button>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => toggleAllGroups(false)}
                                style={{ minWidth: '110px' }}
                            >
                                <i className="bi bi-arrows-collapse me-1"></i>
                                Collapse All
                            </button>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body p-0 function-table">
                                <div className="table-responsive" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                                    <style>{`
                                        .data-row {
                                            transition: background-color 0.15s ease;
                                        }
                                        .data-row:hover {
                                            background-color: #f8f9fa;
                                        }
                                        .table thead th {
                                            background-color: #fff;
                                            color: #333;
                                            position: sticky;
                                            top: 0;
                                            z-index: 10;
                                            font-weight: 600;
                                            border-bottom: 2px solid #dee2e6;
                                            padding: 8px;
                                            font-size: 11px;
                                        }
                                        .table tbody tr td {
                                            border: none;
                                        }
                                        .group-row {
                                            font-weight: 500;
                                        }
                                        .group-row:hover {
                                            opacity: 0.9;
                                        }
                                    `}</style>
                                    <table className="table table-sm mb-0">
                                        <thead>
                                            <tr>
                                                {visibleColumnOrder.map((column, index) => (
                                                    <th key={index} className="text-uppercase">
                                                        {getColumnLabel(column)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groupedNodes.map(node => renderGroupNode(node))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FunctionTimeSegmentsReport;

